import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  VideoSegment,
  getVideoSegmentPlaybackKey,
  locateGlobalTime,
  globalFromLocal,
} from '../utils/segmentedVideo';
import { mediaUrlService } from '../services/mediaUrlService';
import { RefreshCw, AlertCircle, Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import './SegmentedPlayer.css';

interface SegmentedPlayerProps {
  segments: VideoSegment[];
  globalTime: number;
  onGlobalTimeChange: (time: number) => void;
  playing?: boolean;
  muted?: boolean;
  volume?: number;
  onPlayingChange?: (playing: boolean) => void;
  onMutedChange?: (muted: boolean) => void;
  onVolumeChange?: (volume: number) => void;
  onError?: (error: any) => void;
}

const createUrlLoadCancelledError = (): Error => {
  const error = new Error('Segment URL load was cancelled');
  error.name = 'AbortError';
  return error;
};

const isUrlLoadCancelledError = (error: unknown): boolean => (
  error instanceof Error && error.name === 'AbortError'
);

const assertUrlLoadActive = (signal: AbortSignal): void => {
  if (signal.aborted) {
    throw createUrlLoadCancelledError();
  }
};

export const SegmentedPlayer: React.FC<SegmentedPlayerProps> = ({
  segments,
  globalTime,
  onGlobalTimeChange,
  playing = true,
  muted = false,
  volume = 1.0,
  onPlayingChange,
  onMutedChange,
  onVolumeChange,
  onError,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [currentSegId, setCurrentSegId] = useState<string | null>(null);
  const [currentSegmentAuthorityKey, setCurrentSegmentAuthorityKey] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [volumeSliderVisible, setVolumeSliderVisible] = useState(false);
  const [localVolume, setLocalVolume] = useState(volume);
  const [localMuted, setLocalMuted] = useState(muted);
  const [playbackGeneration, setPlaybackGeneration] = useState(0);
  const [hasMountedMediaElement, setHasMountedMediaElement] = useState(false);

  // Track last update source to prevent feedback loops
  const lastSetGlobalTime = useRef(globalTime);
  const lastPlaybackReportedTimeRef = useRef<number | null>(null);
  const urlLoadGenerationRef = useRef(0);
  const recoveredFailureKeyRef = useRef<string | null>(null);
  const recoveryValidationRef = useRef<{
    authorityKey: string;
    failedLocalTime: number;
  } | null>(null);
  const recoveryInFlightRef = useRef<{
    authorityKey: string;
  } | null>(null);
  const pendingRecoverySeekRef = useRef<number | null>(null);
  const preloadingPlaybackKeysRef = useRef(new Set<string>());
  const activeUrlLoadControllersRef = useRef(new Set<AbortController>());
  const desiredPlayingRef = useRef(playing);
  const playRequestGenerationRef = useRef(0);
  const activePlayRequestRef = useRef<{
    generation: number;
    element: HTMLVideoElement;
    authorityKey: string;
  } | null>(null);
  const localVolumeRef = useRef(localVolume);
  const localMutedRef = useRef(localMuted);
  const onGlobalTimeChangeRef = useRef(onGlobalTimeChange);
  const onPlayingChangeRef = useRef(onPlayingChange);
  const onMutedChangeRef = useRef(onMutedChange);
  const onVolumeChangeRef = useRef(onVolumeChange);
  const onErrorRef = useRef(onError);
  const desiredPlaybackAuthorityKeyRef = useRef<string | null>(null);
  const activeSourceUrlRef = useRef(currentUrl);

  desiredPlayingRef.current = playing;
  localVolumeRef.current = localVolume;
  localMutedRef.current = localMuted;
  onGlobalTimeChangeRef.current = onGlobalTimeChange;
  onPlayingChangeRef.current = onPlayingChange;
  onMutedChangeRef.current = onMutedChange;
  onVolumeChangeRef.current = onVolumeChange;
  onErrorRef.current = onError;
  activeSourceUrlRef.current = currentUrl;
  desiredPlaybackAuthorityKeyRef.current = segments.length > 0
    ? getVideoSegmentPlaybackKey(locateGlobalTime(globalTime, segments).segment)
    : null;

  const invalidatePlayRequest = useCallback(() => {
    playRequestGenerationRef.current += 1;
    activePlayRequestRef.current = null;
  }, []);

  const setVideoElementRef = useCallback((element: HTMLVideoElement | null) => {
    const previousElement = videoRef.current;
    videoRef.current = element;
    invalidatePlayRequest();
    if (previousElement && previousElement !== element) {
      // Removing a playing media element does not synchronously release its
      // decoder/network authority in every browser. Explicitly stop it before
      // a replacement can mount so two elements can never own audio.
      previousElement.pause();
      previousElement.removeAttribute('src');
      previousElement.load();
    }
    if (element) {
      element.volume = localVolumeRef.current;
      element.muted = localMutedRef.current;
    }
  }, [invalidatePlayRequest]);

  const isCurrentMediaElement = useCallback((element: HTMLVideoElement | null): boolean => {
    if (!element || element !== videoRef.current || !activeSourceUrlRef.current) {
      return false;
    }
    const expectedSource = activeSourceUrlRef.current;
    const observedSource = element.currentSrc || element.getAttribute('src') || '';
    try {
      return (
        new URL(observedSource, document.baseURI).href
        === new URL(expectedSource, document.baseURI).href
      );
    } catch {
      return observedSource === expectedSource;
    }
  }, []);

  useEffect(() => {
    const activeKeys = new Set(segments.map(getVideoSegmentPlaybackKey));
    Array.from(preloadingPlaybackKeysRef.current).forEach((cacheKey) => {
      if (!activeKeys.has(cacheKey)) {
        preloadingPlaybackKeysRef.current.delete(cacheKey);
      }
    });
  }, [segments]);

  const cancelActiveUrlLoads = useCallback(() => {
    urlLoadGenerationRef.current += 1;
    activeUrlLoadControllersRef.current.forEach(controller => controller.abort());
    activeUrlLoadControllersRef.current.clear();
  }, []);

  const beginUrlLoad = useCallback((cancelExisting: boolean) => {
    if (cancelExisting) {
      cancelActiveUrlLoads();
    }
    const controller = new AbortController();
    activeUrlLoadControllersRef.current.add(controller);
    return {
      controller,
      generation: urlLoadGenerationRef.current,
    };
  }, [cancelActiveUrlLoads]);

  // Resolve once; browser failures get one explicit durable refresh below.
  const loadUrl = useCallback(async (
    seg: VideoSegment,
    signal: AbortSignal,
    forceDurableRefresh = false,
    failedUrl: string | null = null,
  ): Promise<string> => {
    try {
      assertUrlLoadActive(signal);
      const durableSource = seg.originalUri;
      const sourceUri = forceDurableRefresh
        ? durableSource || seg.playbackUrl
        : seg.playbackUrl || durableSource;
      if (!sourceUri) {
        throw new Error('Segment URI not available');
      }

      assertUrlLoadActive(signal);
      const resolvedUrl = (
        forceDurableRefresh
        && durableSource
        && mediaUrlService.needsResolution(durableSource)
      )
        ? await mediaUrlService.refreshFailedUrl(durableSource, failedUrl, {
            videoId: seg.videoId,
            segmentId: seg.mediaScope === 'video' ? undefined : seg.id,
          })
        : await mediaUrlService.resolveUrl(sourceUri, {
            videoId: seg.videoId,
            segmentId: seg.mediaScope === 'video' ? undefined : seg.id,
          });
      assertUrlLoadActive(signal);
      if (!resolvedUrl) {
        throw new Error('Resolved segment URL not available');
      }

      return resolvedUrl;
    } catch (err: any) {
      if (signal.aborted || isUrlLoadCancelledError(err)) {
        throw createUrlLoadCancelledError();
      }
      console.error('Failed to load segment URL:', err);
      throw err;
    }
  }, []);

  // Handle segment switching and seeking
  useEffect(() => {
    if (!segments || segments.length === 0) {
      invalidatePlayRequest();
      cancelActiveUrlLoads();
      setCurrentUrl('');
      setCurrentSegId(null);
      setCurrentSegmentAuthorityKey(null);
      setIsLoading(false);
      setError(null);
      setHasMountedMediaElement(false);
      return;
    }

    const playbackReportedTime = lastPlaybackReportedTimeRef.current;
    const isPlaybackDrivenUpdate = (
      playbackReportedTime !== null
      && Math.abs(playbackReportedTime - globalTime) < 0.001
    );
    lastPlaybackReportedTimeRef.current = null;

    const data = locateGlobalTime(globalTime, segments);
    if (!data) return;

    const seg = data.segment;
    const localTime = data.localTime;
    const segmentAuthorityKey = getVideoSegmentPlaybackKey(seg);
    lastSetGlobalTime.current = globalTime;

    // Reload when either the logical segment or its media authority changes.
    // Asset-ready events and run switches can replace an empty/expired source
    // without changing the segment ID.
    if (
      seg.id !== currentSegId
      || segmentAuthorityKey !== currentSegmentAuthorityKey
    ) {
      // One element owns both picture and sound for the full timeline. Pause
      // the outgoing source before changing authority; the same element is
      // reused for the next segment and resumes only after it is seek-ready.
      invalidatePlayRequest();
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
      setCurrentSegId(seg.id);
      setCurrentSegmentAuthorityKey(segmentAuthorityKey);
      setIsLoading(true);
      setError(null);
      recoveredFailureKeyRef.current = null;
      recoveryValidationRef.current = null;
      recoveryInFlightRef.current = null;
      pendingRecoverySeekRef.current = null;

      setCurrentUrl('');
      const { controller, generation: loadGeneration } = beginUrlLoad(true);

      loadUrl(seg, controller.signal).then(url => {
        if (
          loadGeneration !== urlLoadGenerationRef.current
          || desiredPlaybackAuthorityKeyRef.current !== segmentAuthorityKey
        ) {
          return;
        }
        setHasMountedMediaElement(true);
        setCurrentUrl(url);
      }).catch(err => {
        if (
          loadGeneration !== urlLoadGenerationRef.current
          || desiredPlaybackAuthorityKeyRef.current !== segmentAuthorityKey
          || isUrlLoadCancelledError(err)
        ) {
          return;
        }
        setError('Failed to load video');
        setCurrentUrl('');
        setIsLoading(false);
        onErrorRef.current?.(err);
      }).finally(() => {
        activeUrlLoadControllersRef.current.delete(controller);
      });
    } else if (!isPlaybackDrivenUpdate && videoRef.current) {
      // loadedmetadata applies the latest global time when a source is still
      // opening. Once seekable, a direct assignment is sufficient; delayed
      // retries race newer scrub input and cause visible timeline jumps.
      if (
        videoRef.current.readyState >= 2
        && Math.abs(videoRef.current.currentTime - localTime) > 0.05
      ) {
        videoRef.current.currentTime = localTime;
      }
    }
  }, [
    currentSegId,
    currentSegmentAuthorityKey,
    globalTime,
    beginUrlLoad,
    cancelActiveUrlLoads,
    loadUrl,
    invalidatePlayRequest,
    segments,
  ]);

  const reconcilePlayback = useCallback((element: HTMLVideoElement | null) => {
    if (!element || !isCurrentMediaElement(element)) {
      if (element && element === videoRef.current) element.pause();
      return;
    }

    if (!desiredPlayingRef.current) {
      invalidatePlayRequest();
      element.pause();
      return;
    }

    const authorityKey = desiredPlaybackAuthorityKeyRef.current;
    if (!authorityKey) {
      invalidatePlayRequest();
      element.pause();
      return;
    }

    const activeRequest = activePlayRequestRef.current;

    // A media element is only authoritative once it has decoded the current
    // frame and future data. Starting at loadedmetadata lets audio advance
    // while the initial video seek is still unresolved in some browsers.
    if (
      element.seeking
      || element.readyState < 3 // HAVE_FUTURE_DATA
      || !element.paused
      || (
        activeRequest?.element === element
        && activeRequest.authorityKey === authorityKey
      )
    ) return;

    const generation = playRequestGenerationRef.current + 1;
    playRequestGenerationRef.current = generation;
    activePlayRequestRef.current = { generation, element, authorityKey };
    const isCurrentRequest = () => {
      const current = activePlayRequestRef.current;
      return Boolean(
        current
        && current.generation === generation
        && current.element === element
        && current.authorityKey === authorityKey
        && desiredPlaybackAuthorityKeyRef.current === authorityKey
        && isCurrentMediaElement(element)
      );
    };
    void element.play()
      .then(() => {
        if (!isCurrentRequest()) return;
        if (!desiredPlayingRef.current) element.pause();
      })
      .catch((err) => {
        if (!isCurrentRequest() || !desiredPlayingRef.current) return;
        // Stale play requests are filtered above. A current AbortError means
        // the browser did not start the authoritative element, so keep the
        // controlled state truthful instead of leaving a frozen "playing"
        // state with no future event guaranteed to reconcile it.
        if (err instanceof Error && err.name === 'AbortError') {
          onPlayingChangeRef.current?.(false);
          return;
        }
        console.error('Failed to play:', err);
        onPlayingChangeRef.current?.(false);
      })
      .finally(() => {
        const current = activePlayRequestRef.current;
        if (current?.generation === generation) {
          activePlayRequestRef.current = null;
        }
      });
  }, [invalidatePlayRequest, isCurrentMediaElement]);

  // Metadata owns the initial seek. Playback starts later from the single
  // canplay/seeked reconciliation path, after the target frame is decoded.
  const onLoadedMetadata = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget;
    if (isCurrentMediaElement(element) && segments.length > 0) {
      const data = locateGlobalTime(globalTime, segments);
      const recoverySeek = pendingRecoverySeekRef.current;
      pendingRecoverySeekRef.current = null;
      const targetTime = recoverySeek !== null
        ? recoverySeek
        : data?.localTime;
      if (
        targetTime !== undefined
        && Math.abs(element.currentTime - targetTime) > 0.01
      ) {
        element.currentTime = targetTime;
        return;
      }
      reconcilePlayback(element);
    }
  }, [globalTime, isCurrentMediaElement, reconcilePlayback, segments]);

  const onCanPlay = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isCurrentMediaElement(event.currentTarget)) return;
    setIsLoading(false);
    reconcilePlayback(event.currentTarget);
  }, [isCurrentMediaElement, reconcilePlayback]);

  const onLoadedData = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    // A paused result is ready once its selected frame has decoded. Do not
    // hide that frame behind a spinner while waiting for playback buffering.
    if (isCurrentMediaElement(event.currentTarget) && !event.currentTarget.seeking) {
      setIsLoading(false);
    }
  }, [isCurrentMediaElement]);

  const onSeeked = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isCurrentMediaElement(event.currentTarget)) return;
    if (event.currentTarget.readyState >= 2) setIsLoading(false);
    reconcilePlayback(event.currentTarget);
  }, [isCurrentMediaElement, reconcilePlayback]);

  // Send globalTime updates upward
  const onTimeUpdate = useCallback(() => {
    const activeVideo = videoRef.current;
    if (!activeVideo || !isCurrentMediaElement(activeVideo) || !segments || segments.length === 0) return;

    const currentSeg = segments.find(s => s.id === currentSegId);
    if (!currentSeg) return;

    const localTime = activeVideo.currentTime;
    const recoveryValidation = recoveryValidationRef.current;
    if (
      recoveryValidation
      && recoveredFailureKeyRef.current === recoveryValidation.authorityKey
      && localTime >= recoveryValidation.failedLocalTime + 0.5
      && !activeVideo.seeking
      && !activeVideo.paused
      && activeVideo.readyState >= 3 // HAVE_FUTURE_DATA
    ) {
      // A seek-generated timeupdate is not proof that replacement bytes
      // decoded. Re-open recovery only after active playback has future data
      // and advances beyond the failed point.
      recoveredFailureKeyRef.current = null;
      recoveryValidationRef.current = null;
    }
    const global = globalFromLocal(currentSeg, localTime);

    // Only update if changed significantly to prevent flooding
    if (Math.abs(global - lastSetGlobalTime.current) > 0.1) {
      lastPlaybackReportedTimeRef.current = global;
      lastSetGlobalTime.current = global;
      onGlobalTimeChangeRef.current(global);
    }

    // Prime the next segment's credential when near the end. The shared media
    // service owns freshness and single-flight behavior; this component does
    // not maintain a second URL cache.
    const mediaEnd = (currentSeg.mediaStart || 0) + currentSeg.duration;
    if (currentSeg.mediaStart !== undefined && localTime >= mediaEnd) {
      const nextIndex = segments.findIndex(s => s.id === currentSeg.id) + 1;
      if (nextIndex < segments.length) onGlobalTimeChangeRef.current(segments[nextIndex].start);
      else { activeVideo.pause(); onPlayingChangeRef.current?.(false); }
      return;
    }
    if (localTime >= mediaEnd - 1.0) {
      const nextIndex = segments.findIndex(s => s.id === currentSeg.id) + 1;
      if (nextIndex < segments.length) {
        const nextSeg = segments[nextIndex];
        const nextCacheKey = getVideoSegmentPlaybackKey(nextSeg);
        if (!preloadingPlaybackKeysRef.current.has(nextCacheKey)) {
          preloadingPlaybackKeysRef.current.add(nextCacheKey);
          const { controller } = beginUrlLoad(false);
          loadUrl(nextSeg, controller.signal)
            .catch(preloadError => {
              if (!isUrlLoadCancelledError(preloadError)) {
                console.error(preloadError);
              }
            })
            .finally(() => {
              preloadingPlaybackKeysRef.current.delete(nextCacheKey);
              activeUrlLoadControllersRef.current.delete(controller);
            });
        }
      }
    }
  }, [
    beginUrlLoad,
    currentSegId,
    isCurrentMediaElement,
    loadUrl,
    segments,
  ]);

  // Handle video ended
  const onEnded = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isCurrentMediaElement(event.currentTarget)) return;
    if (!segments || segments.length === 0) return;

    const currentIndex = segments.findIndex(s => s.id === currentSegId);
    if (currentIndex < segments.length - 1) {
      // Move to next segment
      const nextSeg = segments[currentIndex + 1];
      onGlobalTimeChangeRef.current(nextSeg.start);
    } else {
      onPlayingChangeRef.current?.(false);
    }
  }, [segments, currentSegId, isCurrentMediaElement]);

  // The controlled playing value has one media-owner reconciliation path.
  // Source changes do not call play directly: metadata/canplay/seek readiness
  // events must first prove that picture and audio belong to the new source.
  useEffect(() => {
    reconcilePlayback(videoRef.current);
  }, [playing, reconcilePlayback]);

  // Clearing a source during an uncached transition must synchronously stop
  // both decode tracks without replacing the element. React removes the src
  // attribute; load() commits that reset in browsers that otherwise retain the
  // previous resource until a replacement URL arrives.
  useEffect(() => {
    const element = videoRef.current;
    if (currentUrl || !element) return;
    invalidatePlayRequest();
    element.pause();
    element.removeAttribute('src');
    element.load();
  }, [currentUrl, invalidatePlayRequest]);

  // Keep controlled prop changes and user-selected audio state in sync. The
  // local values survive the media-element remount required at segment
  // boundaries, so advancing the timeline never resets volume or mute.
  useEffect(() => {
    setLocalVolume(volume);
  }, [volume]);

  useEffect(() => {
    setLocalMuted(muted);
  }, [muted]);

  // Update volume
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = localVolume;
    }
  }, [localVolume]);

  // Update muted state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = localMuted;
    }
  }, [localMuted]);

  // Replace an expired/exhausted bounded grant from the durable GCS authority.
  // A single failed URL is recovered at most once so a deterministic grant
  // returned within the same issuance window cannot create an error loop.
  const onVideoError = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isCurrentMediaElement(e.currentTarget)) {
      // Detached elements can deliver delayed media errors after a successful
      // remount. They no longer represent the active playback authority.
      return;
    }
    console.error('Video error:', e);
    const locatedSegment = segments.length > 0
      ? locateGlobalTime(globalTime, segments)
      : null;
    const currentSegment = (
      segments.find(segment => segment.id === currentSegId)
      || locatedSegment?.segment
      || null
    );
    const durableSource = currentSegment?.originalUri;
    const failedUrl = currentUrl || e.currentTarget.currentSrc || e.currentTarget.src;
    const currentAuthorityKey = currentSegment
      ? getVideoSegmentPlaybackKey(currentSegment)
      : null;
    const recoveryInFlight = recoveryInFlightRef.current;
    if (
      currentAuthorityKey
      && recoveryInFlight?.authorityKey === currentAuthorityKey
    ) {
      return;
    }
    const failureKey = currentAuthorityKey && failedUrl
      ? currentAuthorityKey
      : null;

    if (
      !currentSegment
      || !durableSource
      || !mediaUrlService.needsResolution(durableSource)
      || !failureKey
      || recoveredFailureKeyRef.current === failureKey
    ) {
      setError('Failed to play video');
      setCurrentUrl('');
      setIsLoading(false);
      onErrorRef.current?.(e);
      return;
    }

    recoveredFailureKeyRef.current = failureKey;
    const failedLocalTime = videoRef.current?.currentTime ?? 0;
    recoveryValidationRef.current = {
      authorityKey: failureKey,
      failedLocalTime,
    };
    recoveryInFlightRef.current = {
      authorityKey: failureKey,
    };
    pendingRecoverySeekRef.current = failedLocalTime;
    setError(null);
    setCurrentUrl('');
    setIsLoading(true);
    const { controller, generation: recoveryGeneration } = beginUrlLoad(true);

    void loadUrl(currentSegment, controller.signal, true, failedUrl)
      .then((refreshedUrl) => {
        if (
          recoveryGeneration !== urlLoadGenerationRef.current
          || desiredPlaybackAuthorityKeyRef.current !== failureKey
        ) {
          return;
        }
        setCurrentUrl(refreshedUrl);
        recoveryInFlightRef.current = null;
        // Force the media element to remount even if deterministic issuance
        // briefly returned the same URL.
        setPlaybackGeneration(generation => generation + 1);
      })
      .catch((refreshError) => {
        if (
          recoveryGeneration !== urlLoadGenerationRef.current
          || desiredPlaybackAuthorityKeyRef.current !== failureKey
          || isUrlLoadCancelledError(refreshError)
        ) {
          return;
        }
        console.error('Failed to refresh segment URL:', refreshError);
        recoveryInFlightRef.current = null;
        pendingRecoverySeekRef.current = null;
        setError('Failed to play video');
        setCurrentUrl('');
        setIsLoading(false);
        onErrorRef.current?.(refreshError);
      })
      .finally(() => {
        activeUrlLoadControllersRef.current.delete(controller);
      });
  }, [
    beginUrlLoad,
    currentSegId,
    currentUrl,
    globalTime,
    isCurrentMediaElement,
    loadUrl,
    segments,
  ]);

  useEffect(() => () => {
    invalidatePlayRequest();
    cancelActiveUrlLoads();
  }, [cancelActiveUrlLoads, invalidatePlayRequest]);

  // Handle click to play/pause
  const handleVideoClick = useCallback(() => {
    const nextPlaying = !desiredPlayingRef.current;
    desiredPlayingRef.current = nextPlaying;
    onPlayingChangeRef.current?.(nextPlaying);
    reconcilePlayback(videoRef.current);
  }, [reconcilePlayback]);

  // Handle volume change
  const handleVolumeChange = useCallback((newVolume: number) => {
    setLocalVolume(newVolume);
    onVolumeChangeRef.current?.(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  }, []);

  // Handle mute toggle
  const handleMuteToggle = useCallback(() => {
    const nextMuted = !localMuted;
    setLocalMuted(nextMuted);
    onMutedChangeRef.current?.(nextMuted);
  }, [localMuted]);

  // Handle fullscreen
  const handleFullscreen = useCallback(() => {
    const playerContainer = document.querySelector('.segmented-player');
    if (playerContainer && playerContainer.requestFullscreen) {
      playerContainer.requestFullscreen();
    }
  }, []);

  // Don't render if no segments
  if (!segments || segments.length === 0) {
    return (
      <div className="segmented-player-empty">
        <AlertCircle size={48} />
        <p>Video playback is unavailable for this media.</p>
      </div>
    );
  }

  // Show error state
  if (error && !currentUrl) {
    return (
      <div className="segmented-player-error">
        <AlertCircle size={24} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div
      className="segmented-player"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => {
        setShowControls(false);
        setVolumeSliderVisible(false);
      }}
    >
      {hasMountedMediaElement && (
        <video
          ref={setVideoElementRef}
          key={playbackGeneration}
          src={currentUrl || undefined}
          controls={false}
          muted={localMuted}
          preload="auto"
          onLoadedMetadata={onLoadedMetadata}
          onLoadedData={onLoadedData}
          onCanPlay={onCanPlay}
          onSeeked={onSeeked}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onError={onVideoError}
          onClick={handleVideoClick}
          playsInline
          hidden={!currentUrl}
          aria-hidden={!currentUrl || undefined}
        />
      )}
      {currentUrl ? (
        <>
          {/* Overlay Controls */}
          <div className={`player-overlay-controls ${showControls ? 'visible' : ''}`}>
            <button className="overlay-control play-pause" onClick={handleVideoClick}>
              {videoRef.current?.paused ? <Play size={32} /> : <Pause size={32} />}
            </button>

            <div className="overlay-control-bar">
              <div
                className="volume-control"
                onMouseEnter={() => setVolumeSliderVisible(true)}
                onMouseLeave={() => setVolumeSliderVisible(false)}
              >
                <button onClick={handleMuteToggle}>
                  {localMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
                {volumeSliderVisible && (
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={localVolume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="volume-slider"
                  />
                )}
              </div>

              <button className="fullscreen-btn" onClick={handleFullscreen}>
                <Maximize size={20} />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="segmented-player-loading">
          <RefreshCw size={24} className="spinning" />
          <p>Loading video...</p>
        </div>
      )}
      {isLoading && (
        <div className="segmented-player-overlay">
          <RefreshCw size={24} className="spinning" />
        </div>
      )}
    </div>
  );
};
